import React, { FC } from 'react'
import {
  Box,
  GridItem,
  HStack,
  Icon,
  SimpleGrid,
  StackDivider,
  StackProps,
  Text,
  VStack
} from '@chakra-ui/react'
import ImageCarousel from '@/app/components/common/ImageCarousel'
import { mainMediasFactory } from '@/core/media/utils/main-medias-factory'
import RealEstateListItem from '@/app/[locale]/(public)/components/RealEstateListItem'
import { FaSquare } from 'react-icons/fa'
import { RealEstateLightDTO } from '@/core/real-estate/typings'

interface RealEstateItemVerticalProps extends StackProps {
  item: RealEstateLightDTO
  onItemClick: (item: RealEstateLightDTO) => void
  noBrokers?: boolean
  noHighlights?: boolean
  /** Preloads the card photo; set it only on the first card of a list. */
  priorityImage?: boolean
}

/** Card photo width: full width on phones, at most one grid column (≤400px) elsewhere. */
const CARD_IMAGE_SIZES = '(max-width: 768px) 100vw, 400px'

const RealEstateItemVertical: FC<RealEstateItemVerticalProps> = ({
  item,
  onItemClick,
  noBrokers,
  noHighlights,
  priorityImage = false,
  ...props
}) => {
  const medias = mainMediasFactory(item.medias)

  return (
    <VStack
      borderColor="border"
      borderWidth={1}
      h="full"
      bg="background.default"
      rounded="md"
      overflow="hidden"
      spacing={0}
      {...props}>
      <VStack spacing={0} w="full" divider={<StackDivider />}>
        <Box
          w="full"
          overflow="hidden"
          sx={{
            aspectRatio: '16 / 9'
          }}>
          <ImageCarousel
            w="full"
            h="full"
            imgSrcs={medias.map((m) => m.url)}
            thumbSrcs={medias.map((m) => m.thumb)}
            sizes={CARD_IMAGE_SIZES}
            priority={priorityImage}
          />
        </Box>
        <RealEstateListItem
          w="full"
          onClick={() => onItemClick(item)}
          flex={1}
          p={2}
          {...{ item, noBrokers }}
        />
      </VStack>
      {!noHighlights && item.highlights.length ? (
        <SimpleGrid
          bg="background.default"
          columns={{ base: 3, md: 2 }}
          w="full"
          gap={{ base: 1, md: 2 }}
          p={{ base: 2, md: 4 }}>
          {item.highlights.map((highlight, index) => (
            <GridItem key={index} flex={1}>
              <HStack spacing={1} w="full">
                <Icon as={FaSquare} color="primary" boxSize={1} />
                <Text fontSize="sm">{highlight}</Text>
              </HStack>
            </GridItem>
          ))}
        </SimpleGrid>
      ) : null}
    </VStack>
  )
}

export default RealEstateItemVertical
